// src/components/Dashboard.js
import React, { useEffect, useState } from 'react';
import { Container, Button, Card, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import useStudyTiming from '../../hooks/useStudyTiming';
import './Dashboard.css';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';

function Dashboard() {
  // ✅ Use `isReady` from useToken to check if synchronization is done
  const { token, isReady, refreshAccessToken, removeToken, getValidToken } = useToken();
  const timing = useStudyTiming();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useProgressRedirect("NOT_STARTED"); // <=== Auto redirect if necessary!

  // ✅ Ensure we wait until the token is ready
  useEffect(() => {
    const checkLogin = async () => {
      const authToken = await getValidToken();
      if (!authToken) {
        removeToken();
        navigate("/", { replace: true });
        return;
      }

      if (localStorage.getItem('role') !== 'PLAYER') {
        navigate('/', { replace: true });
        return;
      }
    };
    checkLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Handle Pre-Experiment Start
  const handleConsent = async () => {
    setLoading(true);
    try {
      const authToken = await getValidToken();

      const res = await fetch("/api"+'/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: "CONSENT" }),
      });

      console.log('Progress update response:', res);

      if (!res.ok) {
        throw new Error("Progress update failed");
      }
      navigate('/consent', { replace: true });
      return;
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
    setLoading(false);
  };

  // ✅ Now safe to render the page
  return (
    <Container className="py-4">
      <ProgressTracker currentStep="not_started" />
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card className="shadow-lg border-0">
        <Card.Body>
          <h2 className="mb-3">
            🔐 The Role of Design Documentation in <br />
            Supporting Web Application Penetration Testing
          </h2>
          <p className="text-muted lead">
            The study explores how access to specific documentation affects the effectiveness and efficiency of web application penetration testing.
          </p>

          <h5 className="mt-4 mb-2">Study Overview:</h5>

          <div className="mb-2" style={{ fontWeight: 700, color: '#0d6efd' }}>
            🕒 Do any time in the 48 hours after receiving your credentials
          </div>
          <ListGroup variant="flush" className="mb-3">
            <ListGroup.Item>
              <strong>1.</strong> Read the study introduction (~5 minutes)
              <div className="text-muted small mt-1">Read the experiment procedure, which explains what you will be asked to do.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>2.</strong> Review and provide informed consent (~5 minutes)
              <div className="text-muted small mt-1">Review the informed consent form and give your consent before anything else begins.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>3.</strong> Complete a short pre-experiment questionnaire (~5 minutes)
              <div className="text-muted small mt-1">Covers your background, penetration testing experience, and familiarity with documentation.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>4.</strong> Review the concept of documentation used in this study (~10 minutes)
              <div className="text-muted small mt-1">A short introduction to the type of documentation available during the experiment.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>5.</strong> Learn how to use the platform to conduct the study (~10 minutes)
              <div className="text-muted small mt-1">A walkthrough of the platform — how to view the documents, reach the target application, and submit or update findings.</div>
            </ListGroup.Item>
          </ListGroup>

          <div className="mb-2" style={{ fontWeight: 700, color: '#b7590d' }}>
            🔒 Unlocks at your scheduled testing time
          </div>
          <ListGroup variant="flush" className="mb-4">
            <ListGroup.Item>
              <strong>6.</strong> Review experimental design documents and take a few quizzes (~15 minutes)
              <div className="text-muted small mt-1">Review the documentation for your assigned application, then answer a short multiple-choice questionnaire so we can confirm the material was understood.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>7.</strong> Perform penetration testing on a web application and submit your findings ({timing.mandatory ? `~${timing.mandatory} minutes, optionally +${timing.extra}` : 'timed session, with an optional extension'})
              <div className="text-muted small mt-1">Carry out the penetration test on the assigned application, submitting findings through the platform as you go. You may choose to continue for {timing.extra ? `around ${timing.extra} minutes` : 'a while'} more.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>8.</strong> Complete a short post-experiment questionnaire (~15 minutes)
              <div className="text-muted small mt-1">Covers the usefulness of the documentation, your workload during the task, and your testing strategy.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>9.</strong> Debriefing and thank you (~5 minutes)
              <div className="text-muted small mt-1">Receive a debrief explaining the purpose of the study.</div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>10.</strong> Optional interview (~30 minutes)
              <div className="text-muted small mt-1">The option to take part in a follow-up interview, arranged for a separate time slot after the experiment.</div>
            </ListGroup.Item>
          </ListGroup>

          <p className="mb-2"><strong>Study Period:</strong> September XX - September XX, 2026</p>
          
          <div className="mb-3">
            <strong>Research Team:</strong>
            <ul className="mt-2 mb-0">
              <li>Anh-Duy Tran, KU Leuven, Belgium<br/>
                <small>Email: <a href="mailto:anh-duy.tran@kuleuven.be">anh-duy.tran@kuleuven.be</a> (primary contact)</small>
              </li>
              <li>Stef Verreydt, KU Leuven, Belgium</li>
              <li>Koen Yskout, KU Leuven, Belgium</li>
              <li>Wouter Joosen, KU Leuven, Belgium</li>
            </ul>
          </div>

          <div className="experiment-note text-muted small border-top pt-3 mt-4">
            <strong>Note:</strong> This experiment is executed and managed by <em>DistriNet @ KU Leuven</em>, following all ethical and compliance standards. All data is used strictly for research purposes.
          </div>

          <Alert variant="warning" className="mt-4 mb-0">
            <strong>⚠️ Important:</strong> Do not attempt to attack, exploit, cheat, or otherwise manipulate the <strong>DocuPen platform itself</strong> (this website). Only the assigned target web application is in scope for penetration testing — your cooperation ensures the integrity and success of this research.
          </Alert>

          <div className="d-grid mt-4">
            <Button
              variant="primary"
              size="lg"
              onClick={handleConsent}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" /> Please wait...
                </>
              ) : (
                '🚀 Read Informed Consent '
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Dashboard;
